import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '../common/Modal';

const DocumentSelectionModal = ({ isOpen, onClose, documents, onConfirm }) => {
  const [selectedDocs, setSelectedDocs] = useState([]);

  // Pre-select suggested primary documents
  useEffect(() => {
    if (documents && documents.length > 0) {
      const suggested = documents
        .filter(doc => doc.suggestedPrimary)
        .map(doc => doc.filename);
      setSelectedDocs(suggested);
    }
  }, [documents]);

  const toggleDoc = (filename) => {
    setSelectedDocs(prev =>
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const selectAll = () => {
    setSelectedDocs(documents.map(doc => doc.filename));
  };

  const clearAll = () => {
    setSelectedDocs([]);
  };

  const handleConfirm = () => {
    const selected = documents.filter(doc => selectedDocs.includes(doc.filename));
    onConfirm(selected);
  };

  const getClassificationColor = (classification) => {
    const colors = {
      primary_rfp: 'bg-green-100 text-green-800 border-green-200',
      amendment: 'bg-blue-100 text-blue-800 border-blue-200',
      supporting: 'bg-slate-100 text-slate-800 border-slate-200',
      administrative: 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return colors[classification] || colors.supporting;
  };

  const getClassificationLabel = (classification) => {
    const labels = {
      primary_rfp: 'Primary RFP',
      amendment: 'Amendment',
      supporting: 'Supporting',
      administrative: 'Administrative'
    };
    return labels[classification] || classification;
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Documents to Analyze"
      widthClassName="max-w-5xl"
    >
      <div className="space-y-4">
        {/* Header Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">
                We found {documents?.length || 0} documents in the ZIP file
              </p>
              <p className="text-blue-700">
                Select the document(s) you want to analyze. Documents marked as "Suggested Primary" are likely to be the main RFP.
              </p>
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        <div className="flex items-center justify-between py-2">
          <div className="text-sm text-slate-600">
            <strong>{selectedDocs.length}</strong> of <strong>{documents?.length || 0}</strong> selected
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              Select All
            </button>
            <span className="text-slate-400">|</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-sm text-slate-600 hover:text-slate-800 font-medium"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Document List */}
        <div className="max-h-96 overflow-y-auto space-y-3 border rounded-lg p-4 bg-slate-50">
          {documents && documents.map((doc) => (
            <div
              key={doc.filename}
              className={`
                border-2 rounded-lg p-4 cursor-pointer transition-all
                ${selectedDocs.includes(doc.filename)
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
                }
                ${doc.suggestedPrimary ? 'ring-2 ring-green-200' : ''}
              `}
              onClick={() => toggleDoc(doc.filename)}
            >
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <div className="pt-1">
                  <input
                    type="checkbox"
                    checked={selectedDocs.includes(doc.filename)}
                    onChange={() => toggleDoc(doc.filename)}
                    className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* File Icon */}
                <div className="flex-shrink-0 pt-1">
                  <FontAwesomeIcon
                    icon={doc.extension === '.pdf' ? 'file-pdf' : 'file-word'}
                    className="text-2xl text-slate-400"
                  />
                </div>

                {/* Document Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 truncate">
                        {doc.filename}
                      </h4>
                      {doc.suggestedPrimary && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200 mt-1">
                          <FontAwesomeIcon icon="star" className="mr-1" />
                          Suggested Primary
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 items-center text-sm">
                    {/* Classification Badge */}
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getClassificationColor(doc.classification)}`}>
                      {getClassificationLabel(doc.classification)}
                    </span>

                    {/* Metadata */}
                    <span className="text-slate-600">
                      {doc.pageCount} pages
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">
                      {formatFileSize(doc.size)}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">
                      Relevance: {doc.relevanceScore}/100
                    </span>
                  </div>

                  {/* Preview */}
                  {doc.preview && (
                    <div className="mt-3 p-2 bg-slate-100 rounded text-xs text-slate-600 line-clamp-2">
                      {doc.preview}...
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedDocs.length === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <FontAwesomeIcon icon="check" />
            <span>Analyze Selected ({selectedDocs.length})</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentSelectionModal;
